import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Wallet, TrendUp, Heart, Eye, ChatCircle, UsersThree, ArrowSquareOut } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import StreakCard from "@/components/StreakCard";
import ConvertPanel from "@/components/ConvertPanel";

const TYPE_LABELS = {
  like_reward: { label: "Likes", icon: Heart, color: "text-pink-400" },
  view_reward: { label: "Views", icon: Eye, color: "text-blue-400" },
  comment_reward: { label: "Comments", icon: ChatCircle, color: "text-purple-400" },
  referral_l1: { label: "Referral L1", icon: UsersThree, color: "text-emerald-400" },
  referral_l2: { label: "Referral L2", icon: UsersThree, color: "text-emerald-300" },
  referral_l3: { label: "Referral L3", icon: UsersThree, color: "text-emerald-200" },
};

export default function Dashboard() {
  const { user, refresh } = useAuth();
  const [earnings, setEarnings] = useState(null);
  const [myPosts, setMyPosts] = useState([]);

  const load = async () => {
    try {
      const [e, p] = await Promise.all([
        api.get("/earnings/mine"),
        api.get("/posts/mine"),
      ]);
      setEarnings(e.data);
      setMyPosts(p.data);
      refresh();
    } catch {
      // ignore
    }
  };

  useEffect(() => {
    load();
     
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="p-6 md:p-8 max-w-6xl mx-auto space-y-6" data-testid="dashboard-page">
      <div>
        <h1 className="font-heading text-3xl md:text-4xl font-black">Your Wallet</h1>
        <p className="text-white/60 mt-1">Track your earnings and content performance.</p>
      </div>

      {/* Credits card */}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="glass rounded-2xl p-6 flex items-center justify-between bg-gradient-to-br from-yellow-500/10 to-orange-500/10 ring-1 ring-yellow-400/30">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-yellow-300">Available Credits</p>
            <p data-testid="wallet-points" className="font-heading text-5xl font-black text-yellow-300 mt-2">
              {user?.points ?? 0}
            </p>
            <p className="text-white/50 text-sm mt-1">Credits power every interaction</p>
            <Link to="/buy-points" data-testid="dashboard-buy-points-link">
              <Button data-testid="dashboard-buy-points-btn" className="mt-3 bg-gradient-to-r from-yellow-500 to-orange-500 text-black hover:brightness-110 font-heading font-bold">
                Buy Credits
              </Button>
            </Link>
          </div>
        </div>

        {/* Balance card */}
        <div className="glass rounded-2xl p-6 flex items-center justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-white/50">Cash Balance</p>
            <p data-testid="wallet-balance" className="font-heading text-5xl font-black text-emerald-400 mt-2">
              ${(user?.balance || 0).toFixed(2)}
            </p>
            <p className="text-white/50 text-sm mt-1">USD earnings from creator share</p>
            <Link to="/withdraw" data-testid="withdraw-link">
              <Button data-testid="dashboard-withdraw-btn" className="mt-3 bg-blue-600 hover:bg-blue-700 gap-2">
                <ArrowSquareOut size={16} weight="bold" /> Withdraw
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* Credits ↔ Cash converter */}
      <ConvertPanel />

      <StreakCard onClaimed={load} />

      {/* Earnings breakdown */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {Object.entries(TYPE_LABELS).map(([key, meta]) => {
          const row = earnings?.breakdown?.find((b) => b._id === key);
          const total = row?.total || 0;
          const count = row?.count || 0;
          const Icon = meta.icon;
          return (
            <Card key={key} className="bg-[#121212] border-white/10">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs uppercase tracking-widest font-bold text-white/50">{meta.label}</p>
                  <Icon size={18} weight="bold" className={meta.color} />
                </div>
                <p className="font-heading text-2xl font-black text-white">${total.toFixed(2)}</p>
                <p className="text-xs text-white/40 mt-1">{count} events</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Recent history */}
      <Card className="bg-[#121212] border-white/10">
        <CardHeader>
          <CardTitle className="font-heading">Recent activity</CardTitle>
        </CardHeader>
        <CardContent>
          {!earnings?.history?.length ? (
            <p className="text-white/50 text-sm text-center py-8">No earnings yet. Start posting or invite friends.</p>
          ) : (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {earnings.history.map((h) => {
                const meta = TYPE_LABELS[h.type] || { label: h.type, icon: TrendUp, color: "text-white" };
                const Icon = meta.icon;
                return (
                  <div key={h.earning_id} className="flex items-center justify-between py-2 border-b border-white/5">
                    <div className="flex items-center gap-3">
                      <Icon size={18} weight="bold" className={meta.color} />
                      <div>
                        <p className="text-sm font-semibold">{meta.label}</p>
                        <p className="text-xs text-white/40">{new Date(h.created_at).toLocaleString()}</p>
                      </div>
                    </div>
                    <p className="font-heading font-bold text-emerald-400">+${h.amount.toFixed(4)}</p>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* My posts */}
      <Card className="bg-[#121212] border-white/10">
        <CardHeader>
          <CardTitle className="font-heading">Your posts</CardTitle>
        </CardHeader>
        <CardContent>
          {myPosts.length === 0 ? (
            <p className="text-white/50 text-sm text-center py-8">No posts yet.</p>
          ) : (
            <div className="space-y-2">
              {myPosts.map((p) => (
                <div
                  key={p.post_id}
                  className="flex items-center justify-between py-3 border-b border-white/5"
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold truncate">{p.caption || `[${p.content_type} post]`}</p>
                    <div className="flex gap-3 text-xs text-white/50 mt-1">
                      <span>{p.view_count} views</span>
                      <span>{p.like_count} likes</span>
                      <span>{p.comment_count} comments</span>
                    </div>
                  </div>
                  <Badge
                    variant="outline"
                    className={
                      p.status === "live"
                        ? "border-emerald-500/40 text-emerald-400"
                        : p.status === "pending_approval"
                        ? "border-yellow-500/40 text-yellow-400"
                        : p.status === "rejected"
                        ? "border-red-500/40 text-red-400"
                        : "border-blue-500/40 text-blue-400"
                    }
                  >
                    {p.status.replace("_", " ")}
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
